/**
 * Shared option field renderer for devcontainer feature configuration.
 *
 * Renders dynamic form fields based on an option_schema definition.
 * Used by both the user-facing FeatureConfigCard and the admin ProjectFeatureManager.
 */

import type { ReactElement } from 'react';
import type { OptionSchemaEntry } from '../schemas/feature.schemas';

interface OptionFieldRendererProps {
  schema: Record<string, OptionSchemaEntry>;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  showAdminBadge?: boolean;
}

function getEffectiveEnum(
  entry: OptionSchemaEntry,
  allValues: Record<string, unknown>,
): string[] | null {
  if (entry.depends_on && entry.enum_map) {
    const raw = allValues[entry.depends_on];
    const parentValue = raw == null ? '' : String(raw as string | number | boolean);
    const filtered = entry.enum_map[parentValue];
    if (filtered && filtered.length > 0) return filtered;
  }
  return entry.enum ?? null;
}

function renderField(
  key: string,
  entry: OptionSchemaEntry,
  value: unknown,
  onChange: (key: string, value: unknown) => void,
  showAdminBadge: boolean,
  allValues: Record<string, unknown>,
): ReactElement {
  const adminBadge =
    showAdminBadge && entry.admin_only ? (
      <span className="option-field__admin-badge">admin only</span>
    ) : null;

  if (entry.type === 'boolean') {
    return (
      <div className="option-field" key={key}>
        <label className="option-field__checkbox-label">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => {
              onChange(key, e.target.checked);
            }}
          />
          <span>{entry.label}</span>
          {adminBadge}
        </label>
        <span className="help-text">{entry.description}</span>
      </div>
    );
  }

  if (entry.type === 'multiselect') {
    const options = getEffectiveEnum(entry, allValues) ?? [];
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div className="option-field" key={key}>
        <label>
          {entry.label}
          {adminBadge}
        </label>
        {options.map((opt) => (
          <label className="option-field__checkbox-label" key={opt}>
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={(e) => {
                // Order is meaningful: the first entry is the workspace's
                // default model, so rebuild from `options` rather than
                // appending — otherwise the default silently changes with
                // the order someone happened to click.
                const next = e.target.checked
                  ? options.filter((o) => selected.includes(o) || o === opt)
                  : selected.filter((o) => o !== opt);
                onChange(key, next);
              }}
            />
            <span>{opt}</span>
          </label>
        ))}
        <span className="help-text">{entry.description}</span>
      </div>
    );
  }

  const effectiveEnum = getEffectiveEnum(entry, allValues);
  if (effectiveEnum && effectiveEnum.length > 0) {
    return (
      <div className="option-field" key={key}>
        <label>
          {entry.label}
          {adminBadge}
        </label>
        <select
          value={String((value ?? entry.default ?? '') as string | number | boolean)}
          onChange={(e) => {
            onChange(key, e.target.value);
          }}
        >
          {effectiveEnum.map((opt) => (
            <option key={opt} value={opt}>
              {entry.enum_labels?.[opt] ?? opt}
            </option>
          ))}
        </select>
        <span className="help-text">{entry.description}</span>
      </div>
    );
  }

  return (
    <div className="option-field" key={key}>
      <label>
        {entry.label}
        {adminBadge}
      </label>
      <input
        type={entry.type === 'number' ? 'number' : 'text'}
        value={String((value ?? entry.default ?? '') as string | number | boolean)}
        onChange={(e) => {
          const newValue =
            entry.type === 'number'
              ? Number(e.target.value)
              : e.target.value;
          onChange(key, newValue);
        }}
        placeholder={
          entry.default != null ? `Default: ${String(entry.default)}` : ''
        }
      />
      <span className="help-text">{entry.description}</span>
    </div>
  );
}

export function OptionFieldRenderer({
  schema,
  values,
  onChange,
  showAdminBadge = false,
}: OptionFieldRendererProps): ReactElement {
  const entries = Object.entries(schema);

  if (entries.length === 0) {
    return (
      <p className="project-features__empty">No configurable options.</p>
    );
  }

  return (
    <>
      {entries.map(([key, entry]) =>
        renderField(key, entry, values[key], onChange, showAdminBadge, values),
      )}
    </>
  );
}
